import { motion, type TargetAndTransition, type Transition } from "framer-motion";
import React from "react";
import usePrefersReducedMotion from "../../shared/hooks/usePrefersReducedMotion";
import KuikaSvg from "../ui-kit/kuika-svg/kuika-svg";

export type AnimatedIconType = "beacon" | "pulse" | "ripple" | "attention" | "spin" | "bounce";

/** The motion props of one looping element: its loop, or — under reduced motion — its resting pose. */
interface ILoopingMotion {
  initial?: false;
  animate: TargetAndTransition;
  transition?: Transition;
}

interface IOnboardingAnimatedIconProps {
  type: AnimatedIconType;
  color?: string;
  size?: number;
}

const DefaultIcon = ({ color, size, name = "info-circle" }: { color: string; size: number; name?: string }) => (
  <div style={{ position: "relative", zIndex: 2, color }}>
    <KuikaSvg name={name} type="common" width={size} height={size} color={color} />
  </div>
);

export const OnboardingAnimatedIcon: React.FC<IOnboardingAnimatedIconProps> = ({
  type,
  color = "#246EFF",
  size = 16
}) => {
  const prefersReducedMotion: boolean = usePrefersReducedMotion();

  /**
   * One looping animation. Under reduced motion the loop never starts: the element takes its
   * resting pose at once (`initial={false}`) and holds it.
   */
  const loop = (animate: TargetAndTransition, transition: Transition, rest: TargetAndTransition): ILoopingMotion =>
    prefersReducedMotion ? { initial: false, animate: rest } : { animate, transition };

  const containerStyle: React.CSSProperties = {
    position: "relative",
    width: size,
    height: size,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  };

  switch (type) {
    case "beacon":
      return (
        <motion.div
          style={{
            position: "relative",
            width: size,
            height: size,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <motion.div
            {...loop(
              { scale: [0, 2.5], opacity: [0.6, 0] },
              { duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.4 },
              { scale: 0, opacity: 0 }
            )}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: size,
              height: size,
              borderRadius: "var(--radius-pill)",
              border: `2px solid ${color}`
            }}
          />

          <motion.div
            {...loop(
              { scale: [0, 2.5], opacity: [0.6, 0] },
              { duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.8 },
              { scale: 0, opacity: 0 }
            )}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: size,
              height: size,
              borderRadius: "var(--radius-pill)",
              border: `2px solid ${color}`
            }}
          />

          <motion.div
            {...loop(
              { scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] },
              { duration: 2, repeat: Infinity, ease: "easeInOut" },
              { scale: 1, opacity: 0.3 }
            )}
            style={{
              position: "absolute",
              top: -4,
              left: -4,
              width: size + 8,
              height: size + 8,
              borderRadius: "var(--radius-pill)",
              background: `radial-gradient(circle, rgba(${color},0.3) 0%, rgba(${color},0) 70%)`,
              filter: "blur(8px)"
            }}
          />

          <motion.div
            {...loop({ scale: [1, 1.05, 1] }, { duration: 2, repeat: Infinity, ease: "easeInOut" }, { scale: 1 })}
            style={{
              position: "relative",
              zIndex: 2,
              width: size,
              height: size,
              color,
              filter: "drop-shadow(0 2px 8px rgba(255,138,0,0.5))"
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size} style={{ opacity: 0.4 }}>
              <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Z" />
            </svg>
          </motion.div>
        </motion.div>
      );

    case "pulse":
      return (
        <motion.div style={containerStyle}>
          <motion.div
            {...loop(
              { scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] },
              { duration: 2, repeat: Infinity, ease: "easeInOut" },
              { scale: 1, opacity: 0.5 }
            )}
            style={{
              position: "absolute",
              width: size,
              height: size,
              borderRadius: "var(--radius-pill)",
              background: color,
              opacity: 0.2
            }}
          />
          <motion.div
            {...loop({ scale: [1, 1.1, 1] }, { duration: 2, repeat: Infinity, ease: "easeInOut" }, { scale: 1 })}
          ></motion.div>
        </motion.div>
      );

    case "ripple":
      return (
        <motion.div style={containerStyle}>
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              {...loop(
                { scale: [1, 3], opacity: [0.5, 0] },
                { duration: 1.5, repeat: Infinity, delay: i * 0.4, ease: "easeOut" },
                { scale: 1, opacity: 0.5 }
              )}
              style={{
                position: "absolute",
                width: size,
                height: size,
                borderRadius: "var(--radius-pill)",
                border: `1px solid ${color}`
              }}
            />
          ))}
        </motion.div>
      );

    case "attention":
      return (
        <motion.div style={containerStyle}>
          <motion.div
            {...loop(
              { rotate: [0, -10, 10, -10, 10, 0] },
              { duration: 0.5, repeat: Infinity, repeatDelay: 2 },
              { rotate: 0 }
            )}
          >
            <DefaultIcon color={color} size={size} name="alert-triangle" />
          </motion.div>
        </motion.div>
      );

    case "spin":
      return (
        <motion.div style={containerStyle}>
          <motion.div
            {...loop({ rotate: 360 }, { duration: 2, repeat: Infinity, ease: "linear" }, { rotate: 0 })}
            style={{
              position: "absolute",
              width: size * 1.4,
              height: size * 1.4,
              borderRadius: "var(--radius-pill)",
              border: `2px dashed ${color}`,
              opacity: 0.6
            }}
          />
        </motion.div>
      );

    case "bounce":
      return (
        <motion.div style={containerStyle}>
          <motion.div {...loop({ y: [-4, 0, -4] }, { duration: 1, repeat: Infinity, ease: "easeInOut" }, { y: 0 })}>
            <DefaultIcon color={color} size={size} name="arrow-down" />
          </motion.div>
          <motion.div
            {...loop(
              { scaleX: [0.8, 1, 0.8], opacity: [0.2, 0.5, 0.2] },
              { duration: 1, repeat: Infinity, ease: "easeInOut" },
              { scaleX: 1, opacity: 0.5 }
            )}
            style={{
              position: "absolute",
              bottom: -4,
              left: size / 2 - 4,
              width: 8,
              height: 2,
              borderRadius: "var(--radius-pill)",
              background: color,
              filter: "blur(1px)"
            }}
          />
        </motion.div>
      );

    default:
      // Default static fallback or empty
      return <DefaultIcon color={color} size={size} />;
  }
};
